import { useQueryClient } from '@tanstack/react-query'
import { Any } from 'cosmjs-types/google/protobuf/any'

import { bridgelessChainInfo } from '@/config'
import { useKeplrWallet } from '@/contexts/KeplrWalletProvider'
import {
  convertAddressPrefix,
  emitter,
  ErrorHandler,
  formatAddress,
  sleep,
} from '@/helpers'
import { pubkeyAnyFromBase64 } from '@/libs'
import {
  buildBecameValidatorMsg,
  buildCreateValidatorMsg,
} from '@/libs/keplr/messages'
import { queryKeys } from '@/queries/keys'

interface BecomeValidatorInputBase {
  validatorPubkeyBase64: string
  moniker: string
  identity?: string
  website?: string
  securityContact?: string
  details?: string
}

type BecomeValidatorPayload =
  | { type: 'amount'; amountAtomic: string }
  | { type: 'nfts'; nftAddresses: string[] }

interface BecomeValidatorInput extends BecomeValidatorInputBase {
  payload: BecomeValidatorPayload
}

function buildMsgForPayload(
  values: BecomeValidatorInput,
  delegatorAddress: string,
  validatorAddress: string,
  pubkeyAny: Any,
) {
  const description = {
    moniker: values.moniker,
    identity: values.identity || undefined,
    website: values.website || undefined,
    security_contact: values.securityContact || undefined,
    details: values.details || undefined,
  }

  if (values.payload.type === 'amount') {
    return buildCreateValidatorMsg({
      delegator_address: delegatorAddress,
      validator_address: validatorAddress,
      pubkey: pubkeyAny,
      value: {
        denom: bridgelessChainInfo.stakeCurrency.coinMinimalDenom,
        amount: values.payload.amountAtomic,
      },
      description,
      // comissions hardcoded to default values in buildCreateValidatorMsg
    })
  }

  return buildBecameValidatorMsg({
    delegator_address: delegatorAddress,
    validator_address: validatorAddress,
    pubkey: pubkeyAny,
    nft_addresses: values.payload.nftAddresses,
    description,
  })
}

export function useBecomeValidatorTx(onSuccess?: () => void) {
  const { address, signAndBroadcast } = useKeplrWallet()
  const queryClient = useQueryClient()

  const submitBecomeValidator = async (
    values: BecomeValidatorInput,
  ): Promise<string> => {
    try {
      if (!address) {
        throw new Error('Wallet not connected')
      }

      // === addresses ===
      const delegatorAddress = address
      const validatorAddress = convertAddressPrefix(address)

      // === pubkey ===
      const PUBKEY_TYPE = 'ed25519'
      const pubkeyAny: Any = pubkeyAnyFromBase64(
        PUBKEY_TYPE,
        values.validatorPubkeyBase64,
      )

      // === msg ===
      const msg = buildMsgForPayload(
        values,
        delegatorAddress,
        validatorAddress,
        pubkeyAny,
      )

      // === broadcast ===
      const txHash = await signAndBroadcast([msg])

      emitter.emit('success', {
        title: 'Validator created',
        message: `TxHash: ${formatAddress(txHash)}`,
      })

      // === polling validators list ===
      const userValoper = convertAddressPrefix(address)
      const validatorsQueryKey = queryKeys.staking.validatorsIsUser(address)
      const MAX_ATTEMPTS = 15

      for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt += 1) {
        await queryClient.invalidateQueries({ queryKey: validatorsQueryKey })

        const current =
          queryClient.getQueryData<{ operator_address: string }[]>(
            validatorsQueryKey,
          ) ?? []

        const found = current.some(v => v.operator_address === userValoper)

        if (found) break

        await sleep(2000)
      }

      onSuccess?.()

      return txHash
    } catch (error) {
      ErrorHandler.process(error, 'Failed to create validator')
      throw error
    }
  }

  return {
    submitBecomeValidator,
  }
}
